import isValid from 'date-fns/isValid';
import parse from 'date-fns/parse';
import { get } from 'lodash';
import { useEffect, useState } from 'react';
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import { filterObject } from 'src/common/utils/format-utils';
import { DEFAULT_PAGE, DEFAULT_PAGE_ITEMS } from '../constants/base';
import { useRouter } from './useRouter';

type FetchThunkProps = (params: Record<string, any>) => (dispatch: Dispatch<any>) => Promise<any>;

interface UseResourcesProps {
  entity: string;
  onGetAll: FetchThunkProps;
  defaultParams?: Record<string, any>;
  paging?: boolean;
}

export const useResources = ({
  entity,
  onGetAll,
  defaultParams = {},
  paging = true,
}: UseResourcesProps) => {
  const dispatch = useDispatch();
  const router = useRouter();
  const [query, setQuery] = useState<Record<string, any>>(router.query);
  const { list } = useSelector((state: RootStateOrAny) => state[entity]);

  const page = get(query, 'page', DEFAULT_PAGE);
  const pageItems = get(query, 'pageItems', DEFAULT_PAGE_ITEMS);

  const handleQueryUpdate = (params: Record<string, any>) => {
    const newQueryParams = filterObject({ ...query, ...params });
    const paramsString = new URLSearchParams(newQueryParams).toString();
    router.push([router.location.pathname, paramsString].join('?'));
  };

  const handleFetch = (params: Record<string, any>) => {
    setQuery(params);
    const defaultPage = get(defaultParams, ['page'], DEFAULT_PAGE);
    const defaultPageItems = get(defaultParams, ['pageItems'], DEFAULT_PAGE_ITEMS);
    const dates = handleDate(params, ['dateFrom', 'dateTo']);
    let fetchQuery = { ...params, ...dates };

    if (paging) {
      const page = get(params, ['page'], defaultPage);
      const pageItems = get(params, ['pageItems'], defaultPageItems);
      fetchQuery = { ...params, ...dates, take: pageItems, skip: parseInt(pageItems) * (page - 1) };
      setQuery({ ...params, page, pageItems });
    }

    if (!list.isLoading) {
      dispatch(onGetAll({ ...defaultParams, ...fetchQuery }));
    }
  };

  const handleFetchFirstPage = () => {
    handleFetch({ ...query, page: 1 });
  };

  const handleFetchLastPage = () => {
    const lastPage = Math.ceil((list.total || 1) / pageItems);
    handleFetch({ ...query, page: lastPage });
  };

  const handleFetchCurrentPage = () => {
    handleFetch({ ...query });
  };

  const handleDate = (params: Record<string, any>, fields: string[]) => {
    const dates: Record<string, any> = {};
    for (const field of fields) {
      const date = get(params, field, null);
      const parsedDate = parse(date, 'dd.mm.yyyy', new Date());
      if (date && isValid(parsedDate)) {
        dates[field] = parsedDate.toISOString();
      }
    }
    return dates;
  };

  useEffect(() => {
    handleFetch(router.query);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router?.query]);

  return {
    list,
    query,
    page,
    pageItems,
    handleQueryUpdate,
    handleFetch,
    handleFetchLastPage,
    handleFetchFirstPage,
    handleFetchCurrentPage,
  };
};
